import { MessagePart, ToolCallPart, ToolResultPart } from './aiAssistantModel'
import { collectFormResolved } from './formRequestModel'
import { questionSetResolved } from './questionSetModel'

export type ToolGroup = { callPart: ToolCallPart; resultPart?: ToolResultPart }
export type GroupedPart = ToolGroup | Exclude<MessagePart, ToolCallPart | ToolResultPart>

/**
 * Pair each tool call with its result so the bubble renders one collapsible
 * group per call. A result with no matching call (a restored transcript can
 * hold one) gets a synthetic `unknown` call so it is still shown.
 */
export function groupParts(parts: MessagePart[]): GroupedPart[] {
  const result: GroupedPart[] = []
  const pairedResultIds = new Set<string>()

  const resultMap = new Map<string, ToolResultPart>()
  for (const part of parts) {
    if (part.type === 'tool_result') {
      resultMap.set(part.tool_call_id, part)
    }
  }

  for (const part of parts) {
    if (part.type === 'tool_call') {
      const matchingResult = resultMap.get(part.tool_call_id)
      if (matchingResult) pairedResultIds.add(matchingResult.tool_call_id)
      result.push({ callPart: part, resultPart: matchingResult })
    } else if (part.type === 'tool_result') {
      if (!pairedResultIds.has(part.tool_call_id)) {
        result.push({
          callPart: {
            type: 'tool_call',
            tool_call_id: part.tool_call_id,
            tool_name: 'unknown',
            arguments: {},
          },
          resultPart: part,
        })
      }
    } else {
      result.push(part)
    }
  }

  return result
}

/** Feature flag that turns on the compact tool-call view. */
export const COMPACT_TOOL_CALLS_FEATURE = 'web-ai-compact-tool-calls'

/** Two or more tool calls in a row, rendered as one collapsible group. */
export type ToolRun = { type: 'tool_run'; groups: ToolGroup[] }
export type RenderItem = GroupedPart | ToolRun

export type ToolCallStatus = 'running' | 'error' | 'no_result' | 'success'

function isToolGroup(item: GroupedPart): item is ToolGroup {
  return 'callPart' in item
}

function isBlankText(item: GroupedPart): boolean {
  return !isToolGroup(item) && item.type === 'text' && item.content.trim() === ''
}

/**
 * Collapse each run of two or more consecutive tool calls into a single
 * `tool_run` item. Text that is only whitespace between two calls does not
 * break a run and is dropped inside it; anything else does. With
 * `compactToolCalls` off, every call stays its own item.
 */
export function collapseToolRuns(items: GroupedPart[], compactToolCalls: boolean): RenderItem[] {
  if (!compactToolCalls) {
    return items
  }

  const result: RenderItem[] = []
  let run: ToolGroup[] = []
  let blanks: GroupedPart[] = []

  const flush = () => {
    if (run.length >= 2) {
      result.push({ type: 'tool_run', groups: run })
    } else {
      result.push(...run)
    }
    result.push(...blanks)
    run = []
    blanks = []
  }

  for (const item of items) {
    if (isToolGroup(item)) {
      run.push(item)
      blanks = []
    } else if (run.length > 0 && isBlankText(item)) {
      blanks.push(item)
    } else {
      flush()
      result.push(item)
    }
  }
  flush()

  return result
}

/** A single call's status: live with no result is still running. */
export function toolCallStatus(resultPart: ToolResultPart | undefined, isHistorical: boolean): ToolCallStatus {
  if (!resultPart) {
    return isHistorical ? 'no_result' : 'running'
  }
  return resultPart.is_error ? 'error' : 'success'
}

/**
 * A run's combined status, in precedence order: error, running, no_result,
 * success. A failure outranks a call still running so it is never hidden
 * behind the spinner.
 */
export function toolRunStatus(groups: ToolGroup[], isHistorical: boolean): ToolCallStatus {
  const statuses = groups.map((g) => toolCallStatus(g.resultPart, isHistorical))
  const precedence: ToolCallStatus[] = ['error', 'running', 'no_result']
  return precedence.find((s) => statuses.includes(s)) ?? 'success'
}

/** `get_job_details` → "Get job details". */
function readableToolName(name: string): string {
  const words = name.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * What a tool-call pill shows. With compact tool calls off, the raw tool
 * name, as before; on, the server's title, or a readable form of the name
 * when the tool has none.
 */
export function toolLabel(part: ToolCallPart, compactToolCalls: boolean): string {
  if (!compactToolCalls) {
    return part.tool_name
  }
  const title = part.tool_title?.trim()
  return title ? title : readableToolName(part.tool_name)
}

/**
 * The collapsed run's label: the call still running and the count while the
 * run is live, otherwise just the count.
 */
export function toolRunLabel(groups: ToolGroup[], isHistorical: boolean, compactToolCalls: boolean): string {
  const running = [...groups]
    .reverse()
    .find((g) => toolCallStatus(g.resultPart, isHistorical) === 'running')
  if (running) {
    return `${toolLabel(running.callPart, compactToolCalls)}… (${groups.length} tools)`
  }
  return `Used ${groups.length} tools`
}

/**
 * Whether a part was rebuilt from chat history rather than streamed live. Only
 * the four presented elements carry a status, and only history sets it.
 */
export function isHistoricalPart(part: MessagePart): boolean {
  return 'status' in part && part.status !== undefined
}

/** Ids of writes whose result has streamed back — they cannot be decided twice. */
function resolvedWriteIds(parts: MessagePart[]): Set<string> {
  return new Set(
    parts
      .filter((p): p is ToolResultPart => p.type === 'tool_result')
      .map((p) => p.tool_call_id)
  )
}

/**
 * Whether the user can still act on a presented element.
 *
 * Three things lock one: the bubble itself is inert (`optionsInteractive` off,
 * e.g. while a response streams); the part came from history, whose pending
 * state does not survive a session restore, so acting on it would only error;
 * or the element already resolved in this session and must not be submitted
 * twice.
 *
 * A resolved element is recognized by the tool result that streamed back: a
 * write by its own id, a submitted write form by the derived id of the write it
 * fed (`${form_id}-<suffix>`), a submitted collect form by the structured
 * values on its own id, and a question set by its structured answers.
 */
export function isPartInteractive(
  part: MessagePart,
  parts: MessagePart[],
  optionsInteractive: boolean
): boolean {
  if (!optionsInteractive || isHistoricalPart(part)) {
    return false
  }

  switch (part.type) {
    case 'option_select':
      return true
    case 'permission_request':
      return !resolvedWriteIds(parts).has(part.tool_call_id)
    case 'permission_group_request':
      // Not the resolved-ids rule the single card uses: a partially failed group
      // has results for the rows that applied while the failures still await a
      // retry-or-cancel, so "any row resolved" would kill the card mid-recovery.
      // The server sends each row's outcome, so read that instead.
      return part.items.some((i) => i.status === 'pending' || i.status === 'failed')
    case 'form_request': {
      // Write mode resolves on the derived id of the write the form fed;
      // collect mode runs no write, so it resolves on the form's own id.
      const writeResolved = parts.some(
        (p) => p.type === 'tool_result' && p.tool_call_id.startsWith(`${part.form_id}-`)
      )
      return !writeResolved && !collectFormResolved(parts, part.form_id)
    }
    case 'question_set':
      return !questionSetResolved(parts, part.question_set_id)
    default:
      return false
  }
}
